import { useContext, useMemo } from 'react';
import { AuthDataProvider } from 'src/components/authProvider';
import {
  SORT_DIRECTION,
  SortMenuOption,
  SortOptions,
} from 'src/components/subHeader/SubHeaderWithSorting.interface';
import { LOCATION_TAG_PAGE_SIZE } from 'src/constants';
import {
  useGetPaginatedLocationTagsWithLocationCount,
  useParamQuery,
} from 'src/hooks';
import { ILocationTag } from 'src/interfaces';
import { lang } from 'src/utils/language';

export const useLocationManageTagPage = () => {
  const { userRole } = useContext(AuthDataProvider);

  const { onChangeParams, getParamsValue } = useParamQuery({
    page: ['1'],
    sort: ['name'],
  });

  const page = useMemo(() => {
    const raw = Number(getParamsValue('page')?.[0] || 1);

    return Number.isInteger(raw) && raw > 0 ? raw : 1;
  }, [getParamsValue]);

  const selectedSortOption: SortMenuOption = useMemo(() => {
    const sortParam = getParamsValue('sort')?.[0] ?? 'name';
    const isDesc = sortParam.startsWith('-');
    const option = isDesc ? sortParam.slice(1) : sortParam;

    return {
      option: option as SortOptions,
      order: isDesc ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC,
    };
  }, [getParamsValue]);

  const toggledOrder =
    selectedSortOption.order === SORT_DIRECTION.DESC
      ? SORT_DIRECTION.ASC
      : SORT_DIRECTION.DESC;

  const filters = useMemo(() => {
    const prefix = selectedSortOption.order === SORT_DIRECTION.ASC ? '' : '-';

    return [`sort=${prefix}${selectedSortOption.option}`];
  }, [selectedSortOption]);

  const { data: paginatedData, isLoading: isLoadingTags } =
    useGetPaginatedLocationTagsWithLocationCount(
      page,
      filters,
      LOCATION_TAG_PAGE_SIZE,
      userRole,
    );
  const rows = useMemo(() => {
    return (paginatedData?.locations ?? []).map((tag: ILocationTag) => ({
      id: tag.id,
      name: tag.name,
      locations_count: tag.locations_count ?? 0,
    }));
  }, [paginatedData]);

  const columns = useMemo(
    () => [
      { id: 'name', label: lang.tag },
      { id: 'locations_count', label: lang.locations },
      { id: 'actions', label: '' },
    ],
    [],
  );

  const sortItems = [
    {
      label: lang.aToZ,
      isSelected: selectedSortOption.option === SortOptions.ALPHABETICALLY,
      onClick: () =>
        handleSortChange({
          option: SortOptions.ALPHABETICALLY,
          order: toggledOrder,
        }),
    },
    {
      label: lang.locations,
      isSelected: selectedSortOption.option === SortOptions.LOCATIONS_COUNT,
      onClick: () =>
        handleSortChange({
          option: SortOptions.LOCATIONS_COUNT,
          order: toggledOrder,
        }),
    },
  ];

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    onChangeParams('page', [`${value}`]);
  };
  const handleSortChange = (newSort: SortMenuOption) => {
    const prefix = newSort.order === SORT_DIRECTION.ASC ? '' : '-';
    onChangeParams('sort', [`${prefix}${newSort.option}`]);
    onChangeParams('page', ['1']);
  };

  return {
    page,
    columns,
    rows,
    totalPages: paginatedData?.totalPages ?? 1,
    totalCount: paginatedData?.totalCount ?? 0,
    isLoadingTags,
    selectedSortOption,
    sortItems,
    handlePageChange,
    handleSortChange,
  };
};
